import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

const pillars = [
  {
    icon: 'ri-video-chat-line',
    number: '01',
    title: 'Virtual Care Delivery',
    description: 'Vetuvia enables veterinarians to extend care beyond the clinic, supporting remote consultations and virtual diagnosis with tools built for real-world effectiveness.'
  },
  {
    icon: 'ri-refresh-line',
    number: '02',
    title: 'Recurring Revenue Models',
    description: 'The platform supports subscription and recurring service structures, helping veterinary practices build more predictable, resilient business models.'
  },
  {
    icon: 'ri-store-2-line',
    number: '03',
    title: 'Curated Commerce',
    description: 'Vetuvia integrates curated product commerce directly into the care experience, creating new revenue streams while keeping pet owners engaged with trusted recommendations.'
  },
  {
    icon: 'ri-microscope-line',
    number: '04',
    title: 'Smarter Remote Diagnosis',
    description: 'Purpose-built tools improve the quality and effectiveness of virtual veterinary interactions, helping practitioners make better-informed decisions remotely.'
  }
];

const outcomes = [
  {
    value: 'Expanded',
    label: 'Care Access',
    description: 'Reaching pet owners beyond the traditional clinic'
  },
  {
    value: 'Flexible',
    label: 'Business Model',
    description: 'Scalable infrastructure for modern veterinary practices'
  },
  {
    value: 'Modern',
    label: 'Care Delivery',
    description: 'Technology designed for the future of veterinary care'
  }
];

const whyMatters = [
  {
    icon: 'ri-line-chart-line',
    title: 'Growing Demand, Limited Capacity',
    description: 'Veterinary care faces increasing demand with constrained traditional capacity. Vetuvia addresses this structural gap with a platform built to scale access without scaling overhead.'
  },
  {
    icon: 'ri-building-4-line',
    title: 'Modernizing the Care Model',
    description: 'Traditional clinic models were not designed for the expectations of modern pet owners. Vetuvia gives veterinarians the infrastructure to meet those expectations on their own terms.'
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Long-Term Relevance in Care',
    description: 'Vetuvia\'s long-term relevance lies not only in commerce or convenience, but in helping enable a stronger model for veterinary care — including technology designed to support better virtual diagnosis over time.'
  }
];

export default function VetuviaPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Vetuvia',
        item: `${siteUrl}/vetuvia`
      }
    ]
  };

  const vetuviaSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Vetuvia | Veterinary Health Technology & Virtual Care Platform | Diivy Portfolio',
    url: `${siteUrl}/vetuvia`,
    description: 'Vetuvia is a platform expanding access to veterinary care while helping veterinarians build flexible, scalable businesses through virtual care, commerce, and technology.',
    about: {
      '@type': 'SoftwareApplication',
      name: 'Vetuvia',
      applicationCategory: 'HealthApplication',
      description: 'Veterinary health technology and commerce platform for modern care delivery',
      featureList: [
        'Virtual Care Delivery',
        'Recurring Revenue Models',
        'Curated Commerce',
        'Smarter Remote Diagnosis'
      ]
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy',
      url: siteUrl,
      description: 'Investment and technology portfolio company focused on health technology, safety, and commerce'
    }
  };

  return (
    <>
      <SEOHead
        title="Vetuvia | Veterinary Health Technology & Virtual Care Platform | Diivy Portfolio"
        description="Vetuvia is a platform expanding access to veterinary care while helping veterinarians build flexible, scalable businesses through virtual care, commerce, and technology."
        keywords="veterinary technology, virtual vet care, pet health platform, veterinary commerce, Vetuvia, Diivy portfolio"
        canonical="/vetuvia"
        ogTitle="Vetuvia | Veterinary Health Technology & Virtual Care Platform"
        ogDescription="Veterinary health technology and commerce for modern care delivery. Vetuvia combines care, commerce, and technology for a more scalable veterinary model."
        ogUrl="/vetuvia"
        twitterTitle="Vetuvia | Veterinary Health Technology & Virtual Care"
        twitterDescription="Veterinary health technology and commerce for modern care delivery."
      />
      <JsonLd data={vetuviaSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-[88px] bg-[#0A0F1E] overflow-hidden">
          {/* Subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px), repeating-linear-gradient(90deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px)'
            }}
          ></div>

          {/* Background image with overlay */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/c64925ac5daa97e2f5fb7b2eebdb1b51.jpg"
              alt="Vetuvia veterinary care environment"
              className="w-full h-full object-cover object-top opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E] via-[#0A0F1E]/90 to-[#0A0F1E]/70"></div>
          </div>

          <div className="relative max-w-[1200px] mx-auto px-12 pt-24 pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              {/* Left — Content */}
              <div className="space-y-10">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Portfolio Company
                  </p>
                </div>

                <div className="space-y-5">
                  <h1 className="text-5xl lg:text-6xl font-light text-white leading-[1.1] tracking-[-0.02em]">
                    Veterinary Health<br />
                    <span className="font-semibold">Technology</span>
                  </h1>
                  <p className="text-[17px] text-[#C9A96E] font-light tracking-wide">
                    Modern care delivery. Scalable practice.
                  </p>
                </div>

                <p className="text-[15px] leading-[1.9] text-white/60 font-light max-w-[480px]">
                  Vetuvia is a platform that expands access to veterinary care while helping veterinarians create more flexible, scalable businesses. Its model combines care delivery, commerce, and technology designed to make virtual diagnosis more effective.
                </p>

                <div className="flex items-center gap-5 pt-2">
                  <a
                    href="https://vetuvia.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[13px] font-semibold text-[#0A0F1E] rounded-sm hover:bg-[#C9A96E]/90 transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
                  >
                    Visit Vetuvia
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#C9A96E]/40 text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
                  >
                    Investor Inquiry
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>

              {/* Right — Visual card */}
              <div className="relative">
                <div className="relative rounded-sm overflow-hidden border border-white/10">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_444ca076ab64bb96a8f835cbc4ef3455_19281171.jpg"
                    alt="Vetuvia veterinary care platform"
                    className="w-full h-[380px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/60 via-transparent to-transparent"></div>

                  {/* Floating label */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm px-5 py-4">
                      <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#C9A96E] mb-1">Vetuvia</p>
                      <p className="text-[13px] text-white/80 font-light">Virtual Care, Commerce &amp; Technology Platform</p>
                    </div>
                  </div>
                </div>

                {/* Decorative corners */}
                <div className="absolute -top-3 -right-3 w-16 h-16 border-t-2 border-r-2 border-[#C9A96E]/30"></div>
                <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-2 border-l-2 border-[#C9A96E]/30"></div>
              </div>
            </div>

            {/* Outcome stats row */}
            <div className="grid grid-cols-3 gap-6 mt-20 pt-16 border-t border-white/5">
              {outcomes.map((item, i) => (
                <div
                  key={i}
                  className="text-center py-8 border border-white/5 rounded-sm bg-white/[0.03] hover:border-[#C9A96E]/30 hover:bg-white/[0.05] transition-all duration-500"
                >
                  <p className="text-4xl font-bold text-[#C9A96E] tracking-[-0.02em]">{item.value}</p>
                  <p className="mt-2 text-[13px] font-semibold text-white/70 tracking-wide">{item.label}</p>
                  <p className="mt-1 text-[12px] text-white/35 font-light">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-32 bg-white">
          <div className="max-w-[1080px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
              {/* Left label */}
              <div className="lg:col-span-4 space-y-6 pt-2">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Overview
                  </p>
                </div>
                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  Built for<br />
                  <span className="font-semibold">Modern Care</span>
                </h2>
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent mt-6"></div>
              </div>

              {/* Right body */}
              <div className="lg:col-span-8 space-y-7 text-[16px] text-gray-600 leading-[1.9] font-light">
                <p>
                  Vetuvia is focused on modernizing how veterinary care is delivered, accessed, and supported. Vetuvia built a platform that helps veterinarians extend care beyond the traditional clinic model while creating new ways to serve pet owners more efficiently.
                </p>
                <p>
                  By combining veterinary care, commerce, and technology, Vetuvia is designed to support a more flexible and scalable approach to care delivery — one that works for both practitioners and the pet owners they serve.
                </p>
                <p>
                  Its approach is built around a simple idea: better tools and better infrastructure can help veterinarians deliver care more effectively while building more resilient businesses.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Vetuvia Does — 4 Pillars */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1200px] mx-auto px-12">
            <div className="text-center space-y-6 mb-20">
              <div className="flex items-center justify-center gap-5">
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                  What Vetuvia Does
                </p>
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              </div>
              <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[560px] mx-auto">
                A Platform Built for<br />
                <span className="font-semibold">Veterinary Scale</span>
              </h2>
              <p className="text-[15px] leading-[1.8] text-gray-500 font-light max-w-[540px] mx-auto">
                Vetuvia supports virtual care, recurring revenue models, curated commerce, and tools intended to improve the effectiveness of remote veterinary interactions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pillars.map((pillar, i) => (
                <div
                  key={i}
                  className="group p-10 bg-white border border-[#0A0F1E]/6 rounded-sm hover:border-[#C9A96E]/40 hover:shadow-sm transition-all duration-400"
                >
                  <div className="flex items-start gap-8">
                    <div className="flex-shrink-0 space-y-4">
                      <span className="block text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]/50">
                        {pillar.number}
                      </span>
                      <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                        <i className={`${pillar.icon} text-xl text-[#C9A96E]`}></i>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-[17px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                        {pillar.title}
                      </h3>
                      <p className="text-[14px] leading-[1.8] text-gray-500 font-light">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="py-32 bg-white">
          <div className="max-w-[1200px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
              {/* Left */}
              <div className="lg:col-span-4 space-y-6 pt-2">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Why It Matters
                  </p>
                </div>
                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  The Problem<br />
                  <span className="font-semibold">Worth Solving</span>
                </h2>
                <p className="text-[15px] leading-[1.8] text-gray-500 font-light">
                  Veterinary care faces growing demand, limited capacity, and increasing pressure on traditional care models. Vetuvia exists to address this directly.
                </p>
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent mt-4"></div>
              </div>

              {/* Right — 3 cards */}
              <div className="lg:col-span-8 space-y-5">
                {whyMatters.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-7 p-8 border border-[#0A0F1E]/6 rounded-sm hover:border-[#C9A96E]/30 hover:bg-[#F7F4EF]/50 transition-all duration-400 group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                      <i className={`${item.icon} text-xl text-[#C9A96E]`}></i>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-[16px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                        {item.title}
                      </h3>
                      <p className="text-[14px] leading-[1.8] text-gray-500 font-light">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Within the Diivy Portfolio */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1080px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
              {/* Left — image */}
              <div className="lg:col-span-5 relative">
                <div className="rounded-sm overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20portfolio%20investment%20concept%20with%20interconnected%20nodes%20and%20lines%20representing%20health%20technology%20ecosystem%2C%20dark%20navy%20background%20with%20warm%20gold%20accent%20geometric%20elements%2C%20sophisticated%20financial%20technology%20visualization%2C%20minimalist%20design%2C%20no%20people%2C%20clean%20and%20elegant%20composition%2C%20warm%20amber%20tones&width=560&height=480&seq=vetuvia-portfolio-01&orientation=portrait"
                    alt="Diivy portfolio strategy"
                    className="w-full h-[400px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/30 to-transparent rounded-sm"></div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-[#C9A96E]/30"></div>
              </div>

              {/* Right — text */}
              <div className="lg:col-span-7 space-y-8">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Within the Diivy Portfolio
                  </p>
                </div>

                <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em]">
                  Part of a Broader<br />
                  <span className="font-semibold">Strategic Focus</span>
                </h2>

                <p className="text-[15px] leading-[1.9] text-gray-600 font-light">
                  As part of the Diivy portfolio, Vetuvia reflects a broader focus on businesses in health technology, safety, and commerce that are built around meaningful problems and long-term value.
                </p>

                <p className="text-[15px] leading-[1.9] text-gray-600 font-light">
                  Within that portfolio, Vetuvia represents Diivy's interest in platforms that combine care delivery, technology, and scalable business infrastructure — built for relevance that extends well beyond the near term.
                </p>

                {/* Pull quote */}
                <div className="relative mt-8 pl-8 border-l-2 border-[#C9A96E]/40">
                  <p className="text-[18px] font-light text-[#0A0F1E] leading-[1.6] italic tracking-[-0.01em]">
                    "Platforms that combine care delivery, technology, and scalable business infrastructure."
                  </p>
                </div>

                <div className="flex items-center gap-5 pt-4">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-3 text-[13px] font-semibold text-[#C9A96E] hover:text-[#0A0F1E] transition-colors duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
                  >
                    About Diivy
                    <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300"></i>
                  </Link>
                  <span className="text-[#0A0F1E]/15">|</span>
                  <a
                    href="https://vetuvia.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-3 text-[13px] font-semibold text-[#C9A96E] hover:text-[#0A0F1E] transition-colors duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
                  >
                    Visit Vetuvia
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="py-24 bg-[#0A0F1E] relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px), repeating-linear-gradient(90deg, transparent, transparent 48px, #C9A96E 48px, #C9A96E 49px)'
            }}
          ></div>
          <div className="relative max-w-[1200px] mx-auto px-12 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-[640px]">
              <h3 className="text-3xl font-light text-white mb-5 leading-[1.4] tracking-[-0.01em]">
                Interested in Vetuvia or the Diivy portfolio?
              </h3>
              <p className="text-[15px] text-white/50 leading-relaxed font-light">
                We welcome conversations with institutional partners and investors focused on health technology, veterinary innovation, and scalable care platforms.
              </p>
            </div>
            <div className="flex items-center gap-5 flex-shrink-0">
              <a
                href="https://vetuvia.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[13px] font-semibold text-[#0A0F1E] rounded-sm hover:bg-[#C9A96E]/90 transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer"
              >
                Visit Vetuvia
                <i className="ri-external-link-line text-sm"></i>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-3.5 border-2 border-[#C9A96E] text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] transition-all duration-300 whitespace-nowrap tracking-wide cursor-pointer group"
              >
                Investor Inquiry
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
